// Enhanced EditBulkQRCodes.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import QRCode from 'qrcode.react';
import Papa from 'papaparse';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

function EditBulkQRCodes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  
  const [bulkGroup, setBulkGroup] = useState(null);
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // State for CSV functionality
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [showUrlUpload, setShowUrlUpload] = useState(false);
  
  // Fetch bulk QR code group data
  useEffect(() => {
    const fetchBulkGroup = async () => {
      try {
        console.log('Fetching bulk group with ID:', id);
        setLoading(true);
        
        const response = await fetch(`/api/bulk-qrcodes/${id}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch bulk QR code group');
        }
        
        const data = await response.json();
        console.log('Bulk group data received:', data);
        setBulkGroup(data);
        setQrCodes(data.qrCodes || []);
        setName(data.name || '');
        
        // Extract base URL from first QR code if available
        if (data.qrCodes && data.qrCodes.length > 0) {
          const firstCode = data.qrCodes[0];
          // Extract the base URL (everything before the slug if present)
          if (firstCode.slug && firstCode.targetUrl.includes(firstCode.slug)) {
            const baseUrlEnd = firstCode.targetUrl.indexOf(firstCode.slug);
            setBaseUrl(firstCode.targetUrl.substring(0, baseUrlEnd));
          } else {
            setBaseUrl(firstCode.targetUrl || '');
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching bulk QR group:', err);
        setError(err.message || 'An error occurred');
        setLoading(false);
      }
    };
    
    fetchBulkGroup();
  }, [id]);
  
  // Function to handle CSV export with current QR code data
  const handleExportTemplate = () => {
    // Create headers row
    const headers = ['Name', 'URL', 'Slug'];
    
    // Create data rows from existing QR codes
    const dataRows = qrCodes.map(qr => [
      qr.qr_name || '',
      // Extract base URL (without slug) if slug exists
      qr.slug && qr.targetUrl.endsWith(qr.slug) 
        ? qr.targetUrl.substring(0, qr.targetUrl.length - qr.slug.length) 
        : qr.targetUrl,
      qr.slug || ''
    ]);
    
    // Combine headers and data
    const csvContent = [headers, ...dataRows];
    
    // Convert to CSV string
    const csv = Papa.unparse(csvContent);
    
    // Create a Blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${name || 'qr_codes'}_template.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Function to handle CSV file upload
  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          console.log('CSV Parse Results:', results);
          
          // Check for required columns
          const fields = results.meta.fields || [];
          const hasUrlColumn = fields.includes('URL');
          
          if (!hasUrlColumn) {
            setError('CSV file must contain a URL column. Please use the template.');
            setCsvData([]);
            return;
          }
          
          // Process the data to normalize field names
          const processedData = results.data.map(row => {
            const processedRow = {};
            
            // Handle Name field (could be "Name" or "Name (optional)")
            if (row.Name !== undefined) {
              processedRow.Name = row.Name;
            } else if (row['Name (optional)'] !== undefined) {
              processedRow.Name = row['Name (optional)'];
            }
            
            // Handle URL field
            if (row.URL !== undefined) {
              processedRow.URL = row.URL;
            }
            
            // Handle Slug field (could be "Slug" or "Slug (optional)")
            if (row.Slug !== undefined) {
              processedRow.Slug = row.Slug;
            } else if (row['Slug (optional)'] !== undefined) {
              processedRow.Slug = row['Slug (optional)'];
            }
            
            return processedRow;
          });
          
          console.log('Processed CSV data:', processedData);
          setCsvData(processedData);
          setError('');
        },
        error: (error) => {
          console.error('CSV parsing error:', error);
          setError('Error parsing CSV file. Please check the format and try again.');
          setCsvData([]);
        }
      });
    }
  };
  
  // Function to update URLs from CSV
  const handleUpdateFromCsv = async () => {
    if (!csvData || csvData.length === 0) {
      setError('Please upload a valid CSV file first.');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      // Process CSV data to create mappings
      const urlMappings = {};
      const nameMappings = {};
      
      // Track if we have a common base URL to set
      let commonBaseUrl = null;
      let baseUrlCount = 0;
      
      // Process each row in the CSV
      csvData.forEach(row => {
        // Skip invalid rows
        if (!row.URL) return;
        
        const url = row.URL.trim();
        const slug = row.Slug ? (row.Slug.startsWith('/') ? row.Slug : '/' + row.Slug) : '';
        
        if (slug) {
          // For entries with slugs, create a mapping
          urlMappings[slug] = url;
        } else {
          // For entries without slugs, count the base URL
          if (!commonBaseUrl) {
            commonBaseUrl = url;
            baseUrlCount = 1;
          } else if (commonBaseUrl === url) {
            baseUrlCount++;
          }
        }
        
        // If we have name mappings (for updating names)
        if (row.Name && slug) {
          nameMappings[slug] = row.Name;
        }
      });
      
      console.log('URL Mappings:', urlMappings);
      console.log('Common Base URL:', commonBaseUrl, 'Count:', baseUrlCount);
      
      // Send update request
      const response = await fetch(`/api/bulk-qrcodes/${id}/update-urls`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          urlMappings, 
          baseUrl: commonBaseUrl,
          nameMappings
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update QR code URLs');
      }
      
      const updatedBulkGroup = await response.json();
      setBulkGroup(updatedBulkGroup);
      setQrCodes(updatedBulkGroup.qrCodes || []);
      
      // Update baseUrl in the UI if we found a common one
      if (commonBaseUrl && baseUrlCount > 0) {
        setBaseUrl(commonBaseUrl);
      }
      
      setSuccessMessage(`Successfully updated ${Object.keys(urlMappings).length} QR codes with custom URLs and ${baseUrlCount} QR codes with the base URL.`);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Error updating QR code URLs:', err);
      setError(err.message || 'An error occurred while updating QR code URLs');
    } finally {
      setSaving(false);
    }
  };
  
  // Function to update all QR codes with new base URL
  const handleUpdateBaseUrl = async () => {
    if (!baseUrl) {
      setError('Please enter a base URL first.');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      // Add console logging to debug
      console.log(`Sending update to: /api/bulk-qrcodes/${id}/update-urls with baseUrl:`, baseUrl);
      
      // Send update request with just the base URL
      const response = await fetch(`/api/bulk-qrcodes/${id}/update-urls`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          baseUrl: baseUrl
        }),
      });
      
      // Check for error responses and get the text first
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Request failed with status ${response.status}: ${errorText}`);
      }
      
      // Now safely parse JSON
      const updatedBulkGroup = await response.json();
      setBulkGroup(updatedBulkGroup);
      setQrCodes(updatedBulkGroup.qrCodes || []);
      
      setSuccessMessage(`Successfully updated all QR codes with base URL: ${baseUrl}`);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Error updating base URL:', err);
      setError(err.message || 'An error occurred while updating the base URL');
    } finally {
      setSaving(false);
    }
  };
  
  // Update group name
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Group name cannot be empty');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const response = await fetch(`/api/bulk-qrcodes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update QR code group');
      }
      
      const updatedBulkGroup = await response.json();
      setBulkGroup(updatedBulkGroup);
      
      setSuccessMessage('Group name updated successfully!');
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error updating group name:', err);
      setError(err.message || 'An error occurred while updating the group name');
    } finally {
      setSaving(false);
    }
  };
  

// Download QR Codes as ZIP
const handleDownloadQRCodes = async () => {
    if (!qrCodes || qrCodes.length === 0) {
      setError('No QR codes to download');
      return;
    }
    
    try {
      setSaving(true); // Show loading state
      
      // Create a new ZIP file
      const zip = new JSZip();
      const folder = zip.folder(name || 'qr_codes');
      
      // Process each QR code
      for (let i = 0; i < qrCodes.length; i++) {
        const qrCode = qrCodes[i];
        
        try {
          console.log(`Processing QR code ${i+1}/${qrCodes.length}:`, qrCode.qr_name);
          
          // Create a high-resolution canvas
          const canvas = document.createElement('canvas');
          const size = 1200; // Higher resolution for better quality
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Get styling info
          const shape = qrCode.shape || bulkGroup?.shape || 'square';
          const bgColor = qrCode.backgroundColor || bulkGroup?.backgroundColor || '#FFFFFF';
          const fgColor = qrCode.foregroundColor || bulkGroup?.foregroundColor || '#000000';
          const textColor = qrCode.textColor || bulkGroup?.textColor || '#000000';
          
          // Draw background with shape
          ctx.fillStyle = bgColor;
          if (shape === 'circle') {
            ctx.beginPath();
            ctx.arc(size/2, size/2, size/2, 0, Math.PI * 2);
            ctx.fill();
          } else if (shape === 'rounded') {
            const radius = size / 10;
            ctx.beginPath();
            ctx.moveTo(radius, 0);
            ctx.lineTo(size - radius, 0);
            ctx.quadraticCurveTo(size, 0, size, radius);
            ctx.lineTo(size, size - radius);
            ctx.quadraticCurveTo(size, size, size - radius, size);
            ctx.lineTo(radius, size);
            ctx.quadraticCurveTo(0, size, 0, size - radius);
            ctx.lineTo(0, radius);
            ctx.quadraticCurveTo(0, 0, radius, 0);
            ctx.fill();
          } else {
            ctx.fillRect(0, 0, size, size);
          }
          
          // Draw QR code
          await new Promise((resolve) => {
            QRCode.toCanvas(
              document.createElement('canvas'), // Temporary canvas for QR data
              `${window.location.origin}/${qrCode.shortId}`,
              {
                width: 200, // Small size for data
                errorCorrectionLevel: 'H',
                margin: 0,
                color: {
                  dark: fgColor,
                  light: 'rgba(0, 0, 0, 0)' // Transparent background
                }
              },
              function(error, tempCanvas) {
                if (error) {
                  console.error(`Error generating QR code for ${qrCode.qr_name}:`, error);
                  resolve();
                  return;
                }
                
                // Get QR code data
                const tempCtx = tempCanvas.getContext('2d');
                const qrData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
                const data = qrData.data;
                const moduleCount = tempCanvas.width;
                
                // Calculate dot size based on canvas size
                const moduleSize = Math.floor(size * 0.7 / moduleCount);
                const startPoint = Math.floor((size - (moduleCount * moduleSize)) / 2);
                
                // Draw custom dots
                ctx.fillStyle = fgColor;
                const dotStyle = qrCode.dotStyle || shape;
                
                for (let y = 0; y < moduleCount; y++) {
                  for (let x = 0; x < moduleCount; x++) {
                    const pixelIndex = (y * moduleCount + x) * 4;
                    const isDark = data[pixelIndex] < 128;
                    
                    if (isDark) {
                      const xPos = startPoint + x * moduleSize;
                      const yPos = startPoint + y * moduleSize;
                      
                      if (dotStyle === 'circle') {
                        ctx.beginPath();
                        ctx.arc(
                          xPos + moduleSize / 2, 
                          yPos + moduleSize / 2, 
                          moduleSize / 2 * 0.9,
                          0, 
                          Math.PI * 2
                        );
                        ctx.fill();
                      } else if (dotStyle === 'rounded') {
                        const radius = moduleSize / 4;
                        ctx.beginPath();
                        ctx.moveTo(xPos + radius, yPos);
                        ctx.lineTo(xPos + moduleSize - radius, yPos);
                        ctx.quadraticCurveTo(xPos + moduleSize, yPos, xPos + moduleSize, yPos + radius);
                        ctx.lineTo(xPos + moduleSize, yPos + moduleSize - radius);
                        ctx.quadraticCurveTo(xPos + moduleSize, yPos + moduleSize, xPos + moduleSize - radius, yPos + moduleSize);
                        ctx.lineTo(xPos + radius, yPos + moduleSize);
                        ctx.quadraticCurveTo(xPos, yPos + moduleSize, xPos, yPos + moduleSize - radius);
                        ctx.lineTo(xPos, yPos + radius);
                        ctx.quadraticCurveTo(xPos, yPos, xPos + radius, yPos);
                        ctx.fill();
                      } else {
                        ctx.fillRect(xPos, yPos, moduleSize, moduleSize);
                      }
                    }
                  }
                }
                
                // Add logo if present
                if (qrCode.logo) {
                  const finishQrAndAddToZip = () => {
                    // Add text elements if enabled
                    // Name
                    if (qrCode.showName && qrCode.qr_name) {
                      const maxNameLength = 25;
                      const displayName = qrCode.qr_name.length > maxNameLength 
                        ? qrCode.qr_name.substring(0, maxNameLength) + '...' 
                        : qrCode.qr_name;
                      
                      ctx.fillStyle = textColor;
                      ctx.font = `bold ${Math.floor(size/20)}px Arial, sans-serif`;
                      ctx.textAlign = 'center';
                      ctx.textBaseline = 'middle';
                      ctx.fillText(displayName, size/2, size - 150);
                    }
                    
                    // URL
                    if (qrCode.showURL) {
                      let displayText = qrCode.targetUrl;
                      
                      // Format special URLs
                      if (displayText.startsWith('tel:')) {
                        displayText = displayText.substring(4);
                      } else if (displayText.startsWith('sms:')) {
                        displayText = displayText.substring(4);
                      } else if (displayText.startsWith('mailto:')) {
                        displayText = displayText.substring(7);
                      }
                      
                      // Limit URL length
                      const maxUrlLength = 40;
                      if (displayText.length > maxUrlLength) {
                        displayText = displayText.substring(0, maxUrlLength) + '...';
                      }
                      
                      ctx.fillStyle = textColor;
                      ctx.font = `${Math.floor(size/40)}px Arial, sans-serif`;
                      ctx.textAlign = 'center';
                      ctx.textBaseline = 'middle';
                      ctx.fillText(displayText, size/2, size - 70);
                    }
                    
                    // Add to ZIP
                    canvas.toBlob(blob => {
                      // Create a filename
                      const fileName = qrCode.qr_name 
                        ? qrCode.qr_name.replace(/[^a-z0-9]/gi, '_') 
                        : `qr_code_${i + 1}`;
                      
                      folder.file(`${fileName}.png`, blob);
                      console.log(`Added QR code to ZIP: ${fileName}.png`);
                      resolve();
                    }, 'image/png');
                  };
                  
                  try {
                    const logoImg = new Image();
                    logoImg.crossOrigin = "Anonymous";
                    
                    logoImg.onload = function() {
                      // Calculate logo size and position
                      const logoSize = size * 0.15;
                      const logoX = (size - logoSize) / 2;
                      const logoY = (size - logoSize) / 2;
                      
                      // Add white background behind logo
                      const padding = size * 0.02;
                      ctx.fillStyle = bgColor;
                      ctx.fillRect(
                        logoX - padding, 
                        logoY - padding, 
                        logoSize + (padding * 2), 
                        logoSize + (padding * 2)
                      );
                      
                      // Draw logo
                      ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
                      console.log(`Added logo to QR code ${i+1}`);
                      
                      finishQrAndAddToZip();
                    };
                    
                    logoImg.onerror = function(e) {
                      console.error(`Failed to load logo for QR ${i+1}:`, e);
                      finishQrAndAddToZip(); // Continue without logo
                    };
                    
                    // Set logo source - handle different URL formats
                    if (qrCode.logo.startsWith('http')) {
                      logoImg.src = qrCode.logo;
                    } else {
                      logoImg.src = `${window.location.origin}${qrCode.logo.startsWith('/') ? '' : '/'}${qrCode.logo}`;
                    }
                    
                    // Set timeout to prevent hanging
                    setTimeout(() => {
                      if (!logoImg.complete) {
                        console.warn(`Logo load timed out for QR ${i+1}`);
                        finishQrAndAddToZip();
                      }
                    }, 5000);
                  } catch (logoError) {
                    console.error(`Error with logo for QR ${i+1}:`, logoError);
                    finishQrAndAddToZip();
                  }
                } else {
                  // No logo, finish QR code
                  const finishQrAndAddToZip = () => {
                    // Add text elements if enabled
                    // Name
                    if (qrCode.showName && qrCode.qr_name) {
                      const maxNameLength = 25;
                      const displayName = qrCode.qr_name.length > maxNameLength 
                        ? qrCode.qr_name.substring(0, maxNameLength) + '...' 
                        : qrCode.qr_name;
                      
                      ctx.fillStyle = textColor;
                      ctx.font = `bold ${Math.floor(size/20)}px Arial, sans-serif`;
                      ctx.textAlign = 'center';
                      ctx.textBaseline = 'middle';
                      ctx.fillText(displayName, size/2, size - 150);
                    }
                    
                    // URL
                    if (qrCode.showURL) {
                      let displayText = qrCode.targetUrl;
                      
                      // Format special URLs
                      if (displayText.startsWith('tel:')) {
                        displayText = displayText.substring(4);
                      } else if (displayText.startsWith('sms:')) {
                        displayText = displayText.substring(4);
                      } else if (displayText.startsWith('mailto:')) {
                        displayText = displayText.substring(7);
                      }
                      
                      // Limit URL length
                      const maxUrlLength = 40;
                      if (displayText.length > maxUrlLength) {
                        displayText = displayText.substring(0, maxUrlLength) + '...';
                      }
                      
                      ctx.fillStyle = textColor;
                      ctx.font = `${Math.floor(size/40)}px Arial, sans-serif`;
                      ctx.textAlign = 'center';
                      ctx.textBaseline = 'middle';
                      ctx.fillText(displayText, size/2, size - 70);
                    }
                    
                    // Add to ZIP
                    canvas.toBlob(blob => {
                      // Create a filename
                      const fileName = qrCode.qr_name 
                        ? qrCode.qr_name.replace(/[^a-z0-9]/gi, '_') 
                        : `qr_code_${i + 1}`;
                      
                      folder.file(`${fileName}.png`, blob);
                      console.log(`Added QR code to ZIP: ${fileName}.png`);
                      resolve();
                    }, 'image/png');
                  };
                  
                  finishQrAndAddToZip();
                }
              }
            );
          });
        } catch (qrError) {
          console.error(`Error processing QR code ${i+1}:`, qrError);
          // Continue with next QR code
        }
      }
      
      // Generate and download the ZIP file
      console.log('Generating ZIP file...');
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${name || 'qr_codes'}.zip`);
      
      setSuccessMessage('QR codes downloaded successfully!');
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error generating QR code ZIP:', err);
      setError('Failed to generate QR code ZIP: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  // Delete the QR code group
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this QR code group? This action cannot be undone.')) {
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      console.log(`Deleting bulk QR code group with ID: ${id}`);
      
      const response = await fetch(`/api/bulk-qrcodes/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        // Try to get error details
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete response error:', errorData);
        throw new Error(errorData.error || `Failed to delete QR code group (${response.status})`);
      }
      
      // Get result for debugging
      const result = await response.json().catch(() => ({}));
      console.log('Delete successful:', result);
      
      // Show success message briefly before navigating
      setSuccessMessage('QR code group deleted successfully!');
      setSuccess(true);
      
      // Navigate back to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1000);
    } catch (err) {
      console.error('Error deleting QR code group:', err);
      setError('Failed to delete QR code group: ' + err.message);
      setSaving(false);
    }
  };
  
  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }
  
  if (error && !bulkGroup) {
    return <div className="bg-red-100 text-red-700 p-4 rounded-lg">{error}</div>;
  }
  
  return (
    <div className="mt-6 max-w-4xl mx-auto px-3 sm:px-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-0">Edit QR Code Group</h1>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-200 text-red-700 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-200 text-green-700 p-4 rounded-lg mb-6">
          {successMessage || 'QR code group updated successfully!'}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="mb-4 bg-white p-4 rounded-lg border">
          <div className="mb-4">
            <label className="block mt-1 text-m text-gray-700 font-semibold mb-1">
              Group Name
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-grow px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleSave}
                disabled={saving || !name.trim()}
                className={`px-4 py-2 rounded font-medium ${
                  saving || !name.trim()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Save Name
              </button>
            </div>
          </div>
          

            <label className="block mt-1 text-m text-gray-700 font-semibold mb-1">
              Base URL Update (All QR Codes)
            </label>
            <p className="text-sm text-gray-400 mt-1 mb-2">
              Update the base URL for all QR codes in this group. For QR codes with slugs, the URL will be {baseUrl}/{'{slug}'}
            </p>
            <div className="flex space-x-2">
              <input
                type="text"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="e.g., https://example.com"
                className="flex-grow px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleUpdateBaseUrl}
                disabled={saving || !baseUrl.trim()}
                className={`px-4 py-2 rounded font-medium ${
                  saving || !baseUrl.trim()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {saving ? 'Updating...' : 'Update All'}
              </button>
            </div>
          </div>
          
          {/* CSV Upload Section */}
          
          <div className="space-y-4 mt-6">
  <button
    onClick={handleDownloadQRCodes}
    disabled={saving}
    className={`w-full py-2 px-4 ${saving ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'} text-white rounded font-semibold transition duration-200`}
  >
    {saving ? 'Processing...' : 'Download QR Codes'}
  </button>
            
  <button
    onClick={handleDelete}
    disabled={saving}
    className={`w-full py-2 px-4 ${saving ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'} text-white rounded font-semibold transition duration-200`}
  >
    {saving ? 'Deleting...' : 'Delete QR Code Group'}
  </button>
</div>
        </div>
        
        {/* QR code preview section */}
        <div>
          <div className="bg-gray-50 border rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center">
              <p className="block mt-1 text-m text-gray-700 font-semibold mb-1">
                Group Properties
              </p>
              <span className="text-sm text-gray-500">
                {qrCodes.length} QR codes
              </span>
            </div>
            <p className="block text-sm text-gray-700 font-medium mt-1 mb-1">
              Shape: <span className="font-medium">{bulkGroup?.shape || 'Square'}</span>
            </p>
            <p className="block text-sm text-gray-700 font-medium mt-1 mb-1">
              Colors: 
              <span className="inline-block w-4 h-4 ml-2 rounded-sm" style={{backgroundColor: bulkGroup?.foregroundColor || '#000000'}}></span>
              <span className="inline-block w-4 h-4 ml-1 rounded-sm" style={{backgroundColor: bulkGroup?.backgroundColor || '#FFFFFF'}}></span>
              <span className="inline-block w-4 h-4 ml-1 rounded-sm" style={{backgroundColor: bulkGroup?.textColor || '#000000'}}></span>
            </p>
            <p className="block text-sm text-gray-700 font-medium mt-1 mb-1">
              Created: <span className="text-sm text-gray-700 font-medium mt-1 mb-1">{new Date(bulkGroup?.createdAt).toLocaleDateString()}</span>
            </p>
          </div>
          
          <div className="max-h-96 overflow-y-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-sm text-gray-700 font-medium uppercase tracking-wider">Preview</th>
                  <th className="px-4 py-2 text-left text-sm text-gray-700 font-medium mt-1 mb-1 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-2 text-left text-sm text-gray-700 font-medium mt-1 mb-1 uppercase tracking-wider">URL/Slug</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {qrCodes.map((qrCode) => (
                  <tr key={qrCode._id}>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="w-12 h-12 flex-shrink-0">
                        <QRCode
                          value={`${window.location.origin}/${qrCode.shortId}`}
                          size={48}
                          fgColor={bulkGroup?.foregroundColor || '#000000'}
                          bgColor={bulkGroup?.backgroundColor || '#FFFFFF'}
                          level="L"
                          renderAs="svg"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                      {qrCode.qr_name}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      <div className="truncate max-w-xs">
                        {qrCode.targetUrl}
                      </div>
                      {qrCode.slug && (
                        <div className="text-xs text-blue-700">
                          Slug: {qrCode.slug}
                        </div>
                      )}
                      <div className="text-xs text-gray-400">
                        Short URL: {window.location.origin}/{qrCode.shortId}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditBulkQRCodes;